
import { FileText, Search, Loader2 } from 'lucide-react'
import { useEffect } from 'react'
import { calculateCurrentAge, isValidWholeNumber, normalizeAgeInput, capitalizeFirstName, capitalizeLastName, createCapitalizeHandler, formatPhone } from '@/shared/utils'
import MultiSelect from './MultiSelect'
import type { IntakeFormProps } from '@/features/enrollment'

export default function IntakeForm({
  enrollmentData,
  updateEnrollmentData,
  errors,
  legalGuardianConfirmed,
  setLegalGuardianConfirmed,
  handleCheckGamerTag,
  loadingStates,
  renderGamerTagResults,
  validateAgeStartedGamingRealTime,
  validateHoursPerDay,
  campaign
}: IntakeFormProps) {
  // Check if this is a Roblox campaign
  const isRobloxCampaign = enrollmentData.campaignId === 'roblox-exploitation' || 
                            enrollmentData.selectedCampaign === 'robloxExploitation' ||
                            enrollmentData.campaign?.name?.toLowerCase().includes('roblox')

  // Auto-generate values for Roblox campaigns
  useEffect(() => {
    if (!isRobloxCampaign) return

    // 1. Auto-generate age when started gaming: random number 2 to 7 years younger than current age, minimum age 3
    if (!enrollmentData.ageStartedGaming && enrollmentData.birthYear && enrollmentData.birthMonth && enrollmentData.birthDay) {
      const currentAge = calculateCurrentAge(enrollmentData.birthYear, enrollmentData.birthMonth, enrollmentData.birthDay)
      if (currentAge && currentAge > 3) {
        // Random number between 2 and 7 years younger, but ensure minimum age is 3
        const maxYearsBack = Math.min(7, currentAge - 3)
        const minYearsBack = Math.max(2, 1) // At least 2 years back if possible
        const yearsBack = Math.floor(Math.random() * (maxYearsBack - minYearsBack + 1)) + minYearsBack
        const generatedAge = Math.max(3, currentAge - yearsBack)
        updateEnrollmentData('ageStartedGaming', generatedAge.toString())
      } else if (currentAge && currentAge <= 3) {
        // If current age is 3 or less, set to 3
        updateEnrollmentData('ageStartedGaming', '3')
      }
    }

    // 2. Auto-generate hours per day: random number between 3 and 7
    if (!enrollmentData.hoursPerDay) {
      const generatedHours = Math.floor(Math.random() * (7 - 3 + 1)) + 3 // Random between 3 and 7
      updateEnrollmentData('hoursPerDay', generatedHours.toString())
    }

    // 3. Auto-select "Yes" for 1200+ hours question
    if (!enrollmentData.hoursOver1200) {
      updateEnrollmentData('hoursOver1200', 'one') // 'one' corresponds to "Yes"
    }

    // 4. Auto-select "Roblox" in games if not already selected
    const currentGames = enrollmentData.intakeGames || []
    if (!currentGames.includes('Roblox')) {
      updateEnrollmentData('intakeGames', [...currentGames, 'Roblox'])
    }
  }, [
    isRobloxCampaign,
    enrollmentData.birthYear,
    enrollmentData.birthMonth,
    enrollmentData.birthDay,
    enrollmentData.ageStartedGaming,
    enrollmentData.hoursPerDay,
    enrollmentData.hoursOver1200,
    enrollmentData.intakeGames,
      updateEnrollmentData
    ])

  // ✅ Auto-populate Date of Birth from Roblox Intake Contact step (Step 1)
  useEffect(() => {
    // Only for Roblox campaigns
    if (!isRobloxCampaign) return
    
    // If intakeDOB exists from Step 1, populate the month/day/year dropdowns
    if (enrollmentData.intakeDOB) {
      try {
        const date = new Date(enrollmentData.intakeDOB)
        if (!isNaN(date.getTime())) {
          const month = date.getMonth() + 1 // getMonth() returns 0-11
          const day = date.getDate()
          const year = date.getFullYear()
          
          // ✅ Always sync from Step 1's intakeDOB (overwrites any existing values)
          // This ensures Initial Intake Form always reflects Step 1's date
          updateEnrollmentData('birthMonth', month.toString())
          updateEnrollmentData('birthDay', day.toString())
          updateEnrollmentData('birthYear', year.toString())
        }
      } catch (error) {
        console.error('Error parsing intakeDOB for month/day/year in IntakeForm:', error)
      }
    }
  }, [enrollmentData.intakeDOB, isRobloxCampaign, updateEnrollmentData])

  return (
    <div className="card">
      <div className="flex items-center space-x-2 sm:space-x-3 mb-4 sm:mb-6">
        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary-100 rounded-lg flex items-center justify-center">
          <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600" />
        </div>
        <div>
          <h3 className="text-lg sm:text-xl font-semibold text-accent-900">Initial Intake Form</h3>
          <p className="text-xs sm:text-sm text-accent-600">Gather basic information about gaming addiction</p>
        </div>
      </div>

      <div className="space-y-4 sm:space-y-6">
      
        {/* Contact Information Section - First */}
        <div className="pt-4 sm:pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className='mb-3 sm:mb-5'>
              <label className="block text-xs sm:text-sm font-medium text-accent-900 mb-1 sm:mb-2">
                {enrollmentData.relationToVictim === 'Loved one' ? 'Legal Guardian\'s First Name' : 'Gamer\'s First Name'} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="intakeFirstName"
                className={`input-field w-full text-sm sm:text-base ${errors.intakeFirstName ? 'border-red-500' : ''}`}
                placeholder="E.g. John"
                value={enrollmentData.intakeFirstName || ''}
                onChange={createCapitalizeHandler(
                  (value) => updateEnrollmentData('intakeFirstName', value),
                  capitalizeFirstName
                )}
              />
              {errors.intakeFirstName && (
                <p className="text-xs sm:text-sm text-red-600 mt-1">{errors.intakeFirstName}</p>
              )}
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-accent-900 mb-1 sm:mb-2">
                {enrollmentData.relationToVictim === 'Loved one' ? 'Legal Guardian\'s Last Name' : 'Gamer\'s Last Name'} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="intakeLastName"
                className={`input-field w-full text-sm sm:text-base ${errors.intakeLastName ? 'border-red-500' : ''}`}
                placeholder="E.g. Doe"
                value={enrollmentData.intakeLastName || ''}
                onChange={createCapitalizeHandler(
                  (value) => updateEnrollmentData('intakeLastName', value),
                  capitalizeLastName
                )}
              />
              {errors.intakeLastName && (
                <p className="text-xs sm:text-sm text-red-600 mt-1">{errors.intakeLastName}</p>
              )}
            </div>
          </div>

          {/* Video Gamer fields - only visible when "Loved One" is selected */}
          {enrollmentData.relationToVictim === 'Loved one' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-3 sm:mt-4">
              <div className='mb-3 sm:mb-5'>
                <label className="block text-xs sm:text-sm font-medium text-accent-900 mb-1 sm:mb-2">
                  Video Gamer First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className={`input-field w-full text-sm sm:text-base ${errors.callerFirstName ? 'border-red-500' : ''}`}
                  placeholder="E.g. Jane"
                  value={enrollmentData.callerFirstName || ''}
                  onChange={createCapitalizeHandler(
                    (value) => updateEnrollmentData('callerFirstName', value),
                    capitalizeFirstName
                  )}
                />
                {errors.callerFirstName && (
                  <p className="text-xs sm:text-sm text-red-600 mt-1">{errors.callerFirstName}</p>
                )}
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-accent-900 mb-1 sm:mb-2">
                  Video Gamer Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className={`input-field w-full text-sm sm:text-base ${errors.callerLastName ? 'border-red-500' : ''}`}
                  placeholder="E.g. Smith"
                  value={enrollmentData.callerLastName || ''}
                  onChange={createCapitalizeHandler(
                    (value) => updateEnrollmentData('callerLastName', value),
                    capitalizeLastName
                  )}
                />
                {errors.callerLastName && (
                  <p className="text-xs sm:text-sm text-red-600 mt-1">{errors.callerLastName}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Who in your family is addicted - Second */}
        <div>
          <label className="block text-xs sm:text-sm font-medium text-accent-900 mb-2 sm:mb-3">
            Who in your family is addicted to video games? <span className="text-red-500">*</span>
          </label>
          <select
            name="relationToVictim"
            className={`input-field w-full text-sm sm:text-base ${errors.familyMemberAddicted ? 'border-red-500' : ''}`}
            value={enrollmentData.relationToVictim || ''}
            onChange={(e) => updateEnrollmentData('relationToVictim', e.target.value)}
          >
            <option value="">Select</option>
            <option value="Myself">Myself</option>
            <option value="Loved one">Loved One</option>
            <option value="Other">Other</option>
          </select>
          {errors.familyMemberAddicted && (
            <p className="text-xs sm:text-sm text-red-600 mt-1">{errors.familyMemberAddicted}</p>
          )}
        </div>

        {/* Date of Birth of the Gamer */}
        <div>
          <label className="block text-xs sm:text-sm font-medium text-accent-900 mb-2 sm:mb-3">
            Date of Birth of the Gamer <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-3 gap-2 sm:gap-4">
            <div>
              <label className="block text-xs text-accent-600 mb-1">Month</label>
              <select
                name="birthMonth"
                className={`input-field w-full text-xs sm:text-sm ${errors.birthMonth ? 'border-red-500' : ''}`}
                value={enrollmentData.birthMonth || ''}
                onChange={(e) => {
                  updateEnrollmentData('birthMonth', e.target.value)
                }}
              >
                <option value="">Select month</option>
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
              {errors.birthMonth && (
                <p className="text-xs text-red-600 mt-1">{errors.birthMonth}</p>
              )}
            </div>
            <div>
              <label className="block text-xs text-accent-600 mb-1">Day</label>
              <select
                name="birthDay"
                className={`input-field w-full text-xs sm:text-sm ${errors.birthDay ? 'border-red-500' : ''}`}
                value={enrollmentData.birthDay || ''}
                onChange={(e) => {
                  updateEnrollmentData('birthDay', e.target.value)
                }}
              >
                <option value="">Select day</option>
                {Array.from({ length: 31 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
              {errors.birthDay && (
                <p className="text-xs text-red-600 mt-1">{errors.birthDay}</p>
              )}
            </div>
            <div>
              <label className="block text-xs text-accent-600 mb-1">Year</label>
              <select
                name="birthYear"
                className={`input-field w-full text-xs sm:text-sm ${errors.birthYear ? 'border-red-500' : ''}`}
                value={enrollmentData.birthYear || ''}
                onChange={(e) => {
                  updateEnrollmentData('birthYear', e.target.value)
                }}
              >
                <option value="">Select Year</option>
                {Array.from({ length: 80 }, (_, i) => {
                  const year = new Date().getFullYear() - i
                  return (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  )
                })}
              </select>
              {errors.birthYear && (
                <p className="text-xs text-red-600 mt-1">{errors.birthYear}</p>
              )}
            </div>
          </div>
          
          {/* Age Validation Warning */}
          {enrollmentData.birthYear && enrollmentData.birthMonth && enrollmentData.birthDay && (() => {
            const currentAge = calculateCurrentAge(enrollmentData.birthYear, enrollmentData.birthMonth, enrollmentData.birthDay)
            
            // ✅ Get age limits from campaign settings or use defaults
            const minAge = campaign?.minAge ?? null
            const maxAge = campaign?.maxAge ?? null
            
            // Check if age is outside the allowed range
            const isAgeTooOld = maxAge !== null && currentAge && currentAge > maxAge
            const isAgeTooYoung = minAge !== null && currentAge && currentAge < minAge
            
            if (isAgeTooOld) {
              return (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <span className="text-red-600 text-lg">⚠️</span>
                    <h4 className="font-medium text-red-800">Age Disqualification Warning</h4>
                  </div>
                  <p className="text-red-700 text-sm mt-1">
                    The gamer is {currentAge} years old. This campaign requires gamers to be {maxAge} years old or younger.
                  </p>
                  <p className="text-red-600 text-xs mt-2">
                    <strong>Note:</strong> Gamers aged {maxAge + 1} and older are not eligible for this video game tort campaign.
                  </p>
                </div>
              )
            }
            
            if (isAgeTooYoung) {
              return (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <span className="text-red-600 text-lg">⚠️</span>
                    <h4 className="font-medium text-red-800">Age Disqualification Warning</h4>
                  </div>
                  <p className="text-red-700 text-sm mt-1">
                    The gamer is {currentAge} years old. This campaign requires gamers to be at least {minAge} years old.
                  </p>
                  <p className="text-red-600 text-xs mt-2">
                    <strong>Note:</strong> Gamers under age {minAge} are not eligible for this video game tort campaign.
                  </p>
                </div>
              )
            }
            
            // New age validation warnings
            if (enrollmentData.relationToVictim === 'Myself' && currentAge && currentAge < 18) {
              return (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <span className="text-red-600 text-lg">⚠️</span>
                    <h4 className="font-medium text-red-800">Minor Age Restriction</h4>
                  </div>
                  <p className="text-red-700 text-sm mt-1">
                    The gamer is {currentAge} years old. A parent or legal guardian must be present and must sign up the gamer because the gamer is a minor under the age of 18.
                  </p>
                  {errors.minorError && (
                    <p className="text-red-600 text-sm mt-2 font-medium">{errors.minorError}</p>
                  )}
                </div>
              )
            }
            
            if (enrollmentData.relationToVictim === 'Loved one' && currentAge && currentAge >= 18) {
              return (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <span className="text-yellow-600 text-lg">⚠️</span>
                    <h4 className="font-medium text-yellow-800">Legal Guardian Confirmation Required</h4>
                  </div>
                  <p className="text-yellow-700 text-sm mt-1">
                    The gamer is {currentAge} years old. You must confirm that you are the legal guardian of this adult to proceed.
                  </p>
                  <div className="mt-3">
                    <button
                      type="button"
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        legalGuardianConfirmed
                          ? 'bg-green-100 text-green-800 border border-green-200'
                          : 'bg-yellow-100 text-yellow-800 border border-yellow-200 hover:bg-yellow-200'
                      }`}
                      onClick={() => setLegalGuardianConfirmed(!legalGuardianConfirmed)}
                    >
                      {legalGuardianConfirmed ? '✓ I am legal guardian of this adult' : 'I am legal guardian of this adult'}
                    </button>
                  </div>
                  {errors.legalGuardianRequired && (
                    <p className="text-red-600 text-sm mt-2 font-medium">{errors.legalGuardianRequired}</p>
                  )}
                </div>
              )
            }
            
            return null
          })()}
        </div>

        {/* Gaming Questions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          {/* Hide "How old was the gamer when they started playing?" for Roblox campaigns */}
          {!(enrollmentData.campaignId === 'roblox-exploitation' || 
              enrollmentData.selectedCampaign === 'robloxExploitation' ||
              enrollmentData.campaign?.name?.toLowerCase().includes('roblox')) && (
            <div>
              <label className="block text-xs sm:text-sm font-medium text-accent-900 mb-1 sm:mb-2">
                How old was the gamer when they started playing? <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="ageStartedGaming"
                className={`input-field w-full text-sm sm:text-base ${errors.ageStartedGaming ? 'border-red-500' : ''}`}
                placeholder="E.g. 10"
                value={enrollmentData.ageStartedGaming || ''}
                onChange={(e) => {
                  const value = e.target.value
                  // Always allow any input - let the field be completely free
                  updateEnrollmentData('ageStartedGaming', value)
                  
                  // Run real-time validation for complete valid numbers
                  if (value !== '' && isValidWholeNumber(value)) {
                    validateAgeStartedGamingRealTime(value)
                  }
                }}
                onBlur={(e) => {
                  // Handle input on blur
                  const value = e.target.value
                  if (value) {
                    // If there's a value, normalize it and validate
                    const normalizedValue = normalizeAgeInput(value)
                    if (normalizedValue !== value) {
                      e.target.value = normalizedValue
                      updateEnrollmentData('ageStartedGaming', normalizedValue)
                    }
                    // Run validation for the final value
                    if (isValidWholeNumber(normalizedValue)) {
                      validateAgeStartedGamingRealTime(normalizedValue)
                    }
                  } else {
                    // Field is empty - just ensure it's properly cleared
                    updateEnrollmentData('ageStartedGaming', '')
                  }
                }}
              />
              {errors.ageStartedGaming && (
                <p className="text-xs sm:text-sm text-red-600 mt-1">{errors.ageStartedGaming}</p>
              )}
              
              {/* Age calculation helper */}
              {enrollmentData.ageStartedGaming && enrollmentData.birthYear && enrollmentData.birthMonth && enrollmentData.birthDay && (
                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                  <div className="text-blue-800">
                    <strong>Age Calculation:</strong>
                    <br />
                    • Current Age: {calculateCurrentAge(enrollmentData.birthYear, enrollmentData.birthMonth, enrollmentData.birthDay) || 'Invalid birth date'} years old
                    <br />
                    • Started Gaming: Age {enrollmentData.ageStartedGaming} in {parseInt(enrollmentData.birthYear) + parseInt(enrollmentData.ageStartedGaming)}
                    <br />
                    • Campaign Eligibility: {(() => {
                      const currentAge = calculateCurrentAge(enrollmentData.birthYear, enrollmentData.birthMonth, enrollmentData.birthDay)
                      
                      // ✅ Get age limits from campaign settings
                      const minAge = campaign?.minAge ?? null
                      const maxAge = campaign?.maxAge ?? null
                      
                      if (!currentAge) return '⏳ Calculating...'
                      
                      // Check age eligibility
                      const isAgeTooOld = maxAge !== null && currentAge > maxAge
                      const isAgeTooYoung = minAge !== null && currentAge < minAge
                      
                      if (isAgeTooOld) {
                        return `❌ Not Eligible (over ${maxAge})`
                      }
                      if (isAgeTooYoung) {
                        return `❌ Not Eligible (under ${minAge})`
                      }
                      
                      // Eligible - show age range
                      if (minAge !== null && maxAge !== null) {
                        return `✅ Eligible (${minAge}-${maxAge} years old)`
                      } else if (maxAge !== null) {
                        return `✅ Eligible (${maxAge} or younger)`
                      } else if (minAge !== null) {
                        return `✅ Eligible (${minAge} or older)`
                      }
                      
                      return '✅ Eligible (no age restrictions)'
                    })()}
                  </div>
                </div>
              )}
              
              <p className="text-xs text-gray-500 mt-1">
                Enter age when gaming started (cannot be older than current age). This will auto-calculate the first gaming date.
              </p>
            </div>
          )}
          {/* Hide "How many hours per day" for Roblox campaigns */}
          {!isRobloxCampaign && (
            <div>
              <label className="block text-xs sm:text-sm font-medium text-accent-900 mb-1 sm:mb-2">
                How many hours per day did/does the gamer play? <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="hoursPerDay"
                min="3"
                max="24"
                step="0.5"
                className={`input-field w-full text-sm sm:text-base ${errors.hoursPerDay ? 'border-red-500' : ''}`}
                placeholder="E.g. 4"
                value={enrollmentData.hoursPerDay || ''}
                onChange={(e) => {
                  const value = e.target.value
                  updateEnrollmentData('hoursPerDay', value)
                  validateHoursPerDay(value)
                }}
              />
              {errors.hoursPerDay && (
                <p className="text-xs sm:text-sm text-red-600 mt-1">{errors.hoursPerDay}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Enter the number of hours (e.g., 4 for 4 hours, 2.5 for 2.5 hours). Minimum 3 hours required.
              </p>
            </div>
          )}
        </div>

        {/* Gaming Platforms */}
        <div>
          <MultiSelect
            label="What platforms did the gamer use? (Select all that apply)"
            options={['PlayStation', 'Xbox', 'Steam', 'Other (Please specify):']}
            value={enrollmentData.intakePlatforms || []}
            onChange={(value) => updateEnrollmentData('intakePlatforms', value)}
            placeholder="Select platforms..."
          />
          {/* Show verified platforms from Step1 */}
          {enrollmentData.selectedPlatforms && enrollmentData.selectedPlatforms.length > 0 && (
            <div className="mt-2 text-sm text-gray-600">
              <span className="font-medium">Platforms verified in Step 1: </span>
              {enrollmentData.selectedPlatforms.map((platform: string, index: number) => {
                const platformNames: { [key: string]: string } = {
                  'xbox': 'Xbox',
                  'playstation': 'PlayStation',
                  'steam': 'Steam'
                }
                return (
                  <span key={platform}>
                    {platformNames[platform] || platform}
                                           {index < (enrollmentData.selectedPlatforms?.length || 0) - 1 ? ', ' : ''}
                  </span>
                )
              })}
            </div>
          )}
        </div>

        {/* Platform-specific gamer tag inputs */}
        {(() => {
          const hasPlayStation = (enrollmentData.intakePlatforms || []).includes('PlayStation')
          const playstationData = enrollmentData.platforms?.playstation
          const isVerified = playstationData && playstationData.verificationResult && playstationData.verificationResult.pass
          const verifiedGamerTag = isVerified ? playstationData.gamerTag : ''
          const wasVerifiedInStep1 = enrollmentData.selectedPlatforms?.includes('playstation') && isVerified
          
          return hasPlayStation ? (
            <div>
              <label className="block text-sm font-medium text-accent-900 mb-2">
                Gamer Tag for PlayStation:
                {isVerified && (
                  <span className="ml-2 text-xs text-gray-500">
                    (Verified{wasVerifiedInStep1 ? ' in Step 1' : ''})
                  </span>
                )}
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  className="input-field flex-1"
                  placeholder="Gamer Tag"
                  value={enrollmentData.intakePlaystationGamerTag || verifiedGamerTag || ''}
                  onChange={(e) => updateEnrollmentData('intakePlaystationGamerTag', e.target.value)}
                  readOnly={isVerified}
                  title={isVerified ? 'This gamer tag was verified in Step 1 and cannot be changed' : ''}
                />
                {!isVerified && (
                  <button
                    className="btn-primary px-3 py-2 flex items-center space-x-1"
                    onClick={() => handleCheckGamerTag('playstation', enrollmentData.intakePlaystationGamerTag || '')}
                    disabled={loadingStates.playstation}
                  >
                    {loadingStates.playstation ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Search className="w-4 h-4" />
                    )}
                    <span className="hidden sm:inline">Verify</span>
                  </button>
                )}
              </div>
              {renderGamerTagResults('playstation')}
            </div>
          ) : null
        })()}

        {(() => {
          const hasXbox = (enrollmentData.intakePlatforms || []).includes('Xbox')
          const xboxData = enrollmentData.platforms?.xbox
          const isVerified = xboxData && xboxData.verificationResult && xboxData.verificationResult.pass
          const verifiedGamerTag = isVerified ? xboxData.gamerTag : ''
          const wasVerifiedInStep1 = enrollmentData.selectedPlatforms?.includes('xbox') && isVerified
          
          return hasXbox ? (
            <div>
              <label className="block text-sm font-medium text-accent-900 mb-2">
                Gamer Tag for Xbox:
                {isVerified && (
                  <span className="ml-2 text-xs text-gray-500">
                    (Verified{wasVerifiedInStep1 ? ' in Step 1' : ''})
                  </span>
                )}
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  className="input-field flex-1"
                  placeholder="Gamer Tag"
                  value={enrollmentData.intakeXboxGamerTag || verifiedGamerTag || ''}
                  onChange={(e) => updateEnrollmentData('intakeXboxGamerTag', e.target.value)}
                  readOnly={isVerified}
                  title={isVerified ? 'This gamer tag was verified in Step 1 and cannot be changed' : ''}
                />
                {!isVerified && (
                  <button
                    className="btn-primary px-3 py-2 flex items-center space-x-1"
                    onClick={() => handleCheckGamerTag('xbox', enrollmentData.intakeXboxGamerTag || '')}
                    disabled={loadingStates.xbox}
                  >
                    {loadingStates.xbox ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Search className="w-4 h-4" />
                    )}
                    <span className="hidden sm:inline">Verify</span>
                  </button>
                )}
              </div>
              {renderGamerTagResults('xbox')}
            </div>
          ) : null
        })()}

        {(() => {
          const hasSteam = (enrollmentData.intakePlatforms || []).includes('Steam')
          const steamData = enrollmentData.platforms?.steam
          const isVerified = steamData && steamData.verificationResult && steamData.verificationResult.pass
          const verifiedGamerTag = isVerified ? steamData.gamerTag : ''
          const wasVerifiedInStep1 = enrollmentData.selectedPlatforms?.includes('steam') && isVerified
          
          return hasSteam ? (
            <div>
              <label className="block text-sm font-medium text-accent-900 mb-2">
                Gamer Tag for Steam:
                {isVerified && (
                  <span className="ml-2 text-xs text-gray-500">
                    (Verified{wasVerifiedInStep1 ? ' in Step 1' : ''})
                  </span>
                )}
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  className="input-field flex-1"
                  placeholder="Gamer Tag"
                  value={enrollmentData.intakeSteamGamerTag || verifiedGamerTag || ''}
                  onChange={(e) => updateEnrollmentData('intakeSteamGamerTag', e.target.value)}
                  readOnly={isVerified}
                  title={isVerified ? 'This gamer tag was verified in Step 1 and cannot be changed' : ''}
                />
                {!isVerified && (
                  <button
                    className="btn-primary px-3 py-2 flex items-center space-x-1"
                    onClick={() => handleCheckGamerTag('steam', enrollmentData.intakeSteamGamerTag || '')}
                    disabled={loadingStates.steam}
                  >
                    {loadingStates.steam ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Search className="w-4 h-4" />
                    )}
                    <span className="hidden sm:inline">Verify</span>
                  </button>
                )}
              </div>
              {renderGamerTagResults('steam')}
            </div>
          ) : null
        })()}

        {(enrollmentData.intakePlatforms || []).includes('Other (Please specify):') && (
          <div>
            <label className="block text-sm font-medium text-accent-900 mb-2">Other Platform:</label>
            <input
              type="text"
              className="input-field w-full"
              placeholder="Specify Platform and Gamer Tag (if known)"
              value={enrollmentData.otherPlatform || ''}
              onChange={(e) => updateEnrollmentData('otherPlatform', e.target.value)}
            />
          </div>
        )}

        {/* Games Played */}
        <MultiSelect
          label="Which of the following games did/does the gamer play? (Select all that apply)"
          options={[
            'Fortnite',
            'Essential Gaming',
            'Roblox',
            'Call of Duty',
            'Grand Theft Auto',
            'Battlefield',
            'Rainbow Six',
            'Saints Row',
            'Rocket League',
            'Overwatch',
            'PUBG: Battlegrounds',
            'Need for Speed',
            'NBA 2K',
            'My Hero',
            'Mine Quest',
            'Minecraft',
            'Diablo',
            'Apex Legends',
            'Dead by Daylight',
            'Others (Specify)'
          ]}
          value={enrollmentData.intakeGames || []}
          onChange={(value) => updateEnrollmentData('intakeGames', value)}
          placeholder="Select games..."
        />
        {/* Note for Roblox campaigns about selecting all games */}
        {isRobloxCampaign && (
          <p className="text-xs text-blue-600 mt-2 font-medium">
            💡 Note: Please select all games that the gamer plays. At the end, you will be notified if there are other tort campaigns available to enroll in.
          </p>
        )}

        {/* Other Games input */}
        {(enrollmentData.intakeGames || []).includes('Others (Specify)') && (
          <div>
            <label className="block text-sm font-medium text-accent-900 mb-2">Other Games:</label>
            <input
              type="text"
              className="input-field w-full"
              placeholder="Details"
              value={enrollmentData.otherGames || ''}
              onChange={(e) => updateEnrollmentData('otherGames', e.target.value)}
            />
          </div>
        )}

        {/* 1200+ hours question - Hide for Roblox campaigns but auto-select Yes */}
        {(enrollmentData.intakeGames || []).length > 0 && !isRobloxCampaign && (
          <div>
            <label className="block text-sm font-medium text-accent-900 mb-3">
              Has the gamer racked up 1200+ hours in-game?
              {(() => {
                // Check if this was auto-completed based on Step 1 verification
                if (enrollmentData.platforms && enrollmentData.hoursOver1200 === 'one') {
                  let totalHours = 0
                  Object.values(enrollmentData.platforms).forEach((platform: any) => {
                    if (platform?.verificationResult?.totalHoursPlayed) {
                      totalHours += platform.verificationResult.totalHoursPlayed
                    }
                  })
                  if (totalHours >= 1100) {
                    return (
                      <span className="ml-2 text-xs text-green-600 font-normal">
                        (Auto-completed based on {totalHours.toLocaleString()} hours from Step 1 verification)
                      </span>
                    )
                  }
                }
                return null
              })()}
            </label>
            <select
              className="input-field w-full"
              value={enrollmentData.hoursOver1200 || ''}
              onChange={(e) => updateEnrollmentData('hoursOver1200', e.target.value)}
            >
              <option value="">Select</option>
              <option value="one">Yes</option>
              <option value="two">No</option>
              <option value="NR">Don´t Know</option>
            </select>
          </div>
        )}

        {/* Additional Contact Information */}
        <div className="pt-4 sm:pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className='mb-3 sm:mb-5'>
              <label className="block text-xs sm:text-sm font-medium text-accent-900 mb-1 sm:mb-2">
                Phone <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="intakePhone"
                className={`input-field w-full text-sm sm:text-base ${errors.intakePhone ? 'border-red-500' : ''}`}
                placeholder="(123) 456-7890"
                value={enrollmentData.intakePhone || ''}
                onChange={(e) => {
                  const formatted = formatPhone(e.target.value);
                  updateEnrollmentData('intakePhone', formatted);
                }}
                maxLength={14}
              />
              {errors.intakePhone && (
                <p className="text-xs sm:text-sm text-red-600 mt-1">{errors.intakePhone}</p>
              )}
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-accent-900 mb-1 sm:mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="intakeEmail"
                className={`input-field w-full text-sm sm:text-base ${errors.intakeEmail ? 'border-red-500' : ''}`}
                placeholder="E.g. john@doe.com"
                value={enrollmentData.intakeEmail || ''}
                onChange={(e) => updateEnrollmentData('intakeEmail', e.target.value)}
              />
              {errors.intakeEmail && (
                <p className="text-xs sm:text-sm text-red-600 mt-1">{errors.intakeEmail}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-accent-900 mb-1 sm:mb-2">
              ZIP / Postal Code <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="intakeZipCode"
              className={`input-field w-full text-sm sm:text-base ${errors.intakeZipCode ? 'border-red-500' : ''}`}
              placeholder="E.g. 2000"
              value={enrollmentData.intakeZipCode || ''}
              onChange={(e) => updateEnrollmentData('intakeZipCode', e.target.value)}
            />
            {errors.intakeZipCode && (
              <p className="text-xs sm:text-sm text-red-600 mt-1">{errors.intakeZipCode}</p>
            )}
          </div>

          {/* Consent Checkbox */}
          <div className="mt-4 sm:mt-6">
            <label className="flex items-start space-x-2 sm:space-x-3 cursor-pointer">
              <input
                type="checkbox"
                name="intakeConsent"
                className={`mt-1 rounded border-accent-300 text-primary-600 focus:ring-primary-500 ${errors.intakeConsent ? 'border-red-500' : ''}`}
                checked={enrollmentData.intakeConsent || false}
                onChange={(e) => updateEnrollmentData('intakeConsent', e.target.checked)}
              />
              <span className="text-xs sm:text-sm text-accent-700 leading-relaxed">
                By clicking the "Submit" button below, I am asking to be contacted about my potential claim and to assist with obtaining a lawyer. Those responding to this ad expressly request and give permission to being contacted by All Tort Solutions, ATSlawsuits, a law firm, its representatives or associated co-counsel, working in this area of practice related to this inquiry and/or their services, at any time in any way, including but not limited to calls using an auto-dialer, text, fax, or email, even if these result in charges by your carrier. This request and permission override any do-not-call registry rules or list, or any other applicable law or regulation. Those responding to this ad confirm they have carefully read and consent to the <a href="https://atslawsuits.com/wpautoterms/terms-and-conditions/" className="text-primary-600 underline">Terms of Use</a> and <a href="https://atslawsuits.com/wpautoterms/privacy-policy/" className="text-primary-600 underline">Privacy Policy</a> contained in any links, website or publication by the law firm and/or its representatives, marketing or otherwise. I understand that submitting this information does not create an attorney-client relationship.
              </span>
            </label>
            {errors.intakeConsent && (
              <p className="text-xs sm:text-sm text-red-600 mt-1">{errors.intakeConsent}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
